import React, { useEffect, useRef, useState } from "react";
import "../Styles/Travelers_favorite.css";
import RajasthanImage from "../Images/rajasthan_pic.png";
import api from "../Api.js";
import { useNavigate } from "react-router-dom";
import DateFormatter from "./Dateformatter";
import Skeleton from "@mui/material/Skeleton";

const SkeletonCard = () => {
  return (
    <div className="tf-card">
      <div className="tf-image">
        <Skeleton variant="rectangular" width="100%" height="100%" />
      </div>
      <div className="tf-content">
        <Skeleton variant="text" width="80%" height={25} />
        <Skeleton variant="text" width="60%" height={20} />
        <Skeleton variant="text" width="90%" height={20} />
        <div className="tf-footer">
          <Skeleton variant="rectangular" width={100} height={35} />
          <Skeleton variant="text" width={120} height={20} />
        </div>
      </div>
    </div>
  );
};

function Travelers_favorite() {
  const [groupData, setGroupData] = useState([]);
  const [isLoading, setIsLoading] = useState(true); 
  const [isFetchingMore, setIsFetchingMore] = useState(false); 
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  const sliderRef = useRef(null);
  const navigate = useNavigate();

  // 1. SYNCHRONOUS LOCK: Prevents multiple API calls instantly
  const isFetchingRef = useRef(false); 
  const LIMIT = 4; 

  const fetchGroupData = async (currentPage, isInitial = false) => {
    // 2. Block the call if a fetch is already in progress
    if (isFetchingRef.current) return; 
    isFetchingRef.current = true; // Lock it synchronously

    if (isInitial) setIsLoading(true);
    else setIsFetchingMore(true);

    try {
      const response = await api.get(
        `/tours/details?trip_type_id=5&page=${currentPage}&limit=${LIMIT}`
      );
      
      const newData = response?.data?.data || [];

      // If backend ignores pagination and sends everything, this prevents infinite loops
      if (newData.length === 0 || newData.length < LIMIT) {
        setHasMore(false);
      }

      if (isInitial) {
        setGroupData(newData);
      } else {
        // Prevent duplicate keys if backend sends overlapping data
        setGroupData((prev) => {
          const existingIds = new Set(prev.map(item => item.tour.id));
          const uniqueNewData = newData.filter(item => !existingIds.has(item.tour.id));
          return [...prev, ...uniqueNewData];
        });
      }
    } catch (err) {
      console.log("Error fetching group tours", err);
      // Optional: Add a retry mechanism or show an error toast here
    } finally {
      isFetchingRef.current = false; // Unlock
      setIsLoading(false);
      setIsFetchingMore(false);
    }
  };

  // Initial Fetch
  useEffect(() => {
    fetchGroupData(1, true);
  }, []);

  // Fetch when page changes
  useEffect(() => {
    if (page > 1) {
      fetchGroupData(page, false);
    }
  }, [page]);

  // 3. SCROLL LISTENER: Triggers only when user scrolls near the end
  const handleScroll = () => {
    if (!sliderRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
    
    // Check if scrolled within 100px of the right edge
    if (scrollLeft + clientWidth >= scrollWidth - 100) {
      // Check refs and states synchronously
      if (!isFetchingRef.current && hasMore && !isLoading && !isFetchingMore) {
        setPage((prevPage) => prevPage + 1);
      }
    }
  };

  const scrollSlider = (direction) => {
    const scrollAmount = 420;
    if (sliderRef.current) {
      sliderRef.current.scrollBy({
        left: direction === "next" ? scrollAmount : -scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const createSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const handleGroupDetails = (item) => {
    const slug = createSlug(item?.tour?.title);
    const id = item?.tour?.id;
    const random = Math.floor(100 + Math.random() * 900);
    const encodedId = id + random;
    const urlPart = `${slug}-${encodedId}-${random}`;
    navigate(`/tour/grouptour/${urlPart}`);
  };

  return (
    <section className="tf-wrapper">
      <div className="tf-container">
        {/* Header */}
        <div className="tf-header">
          <h3>🔥 Trending Group Tours</h3>
          <div className="tf-actions">
            <button
              className="tf-nav-btn"
              onClick={() => scrollSlider("prev")}
              aria-label="Previous"
            >
              ◀
            </button>
            <span
              className="tf-show-more"
              onClick={() => navigate("/tour/grouptour")}
            >
              + Show more
            </span>
            <button
              className="tf-nav-btn"
              onClick={() => scrollSlider("next")}
              aria-label="Next"
            >
              ▶
            </button>
          </div>
        </div>

        {/* Slider - Added onScroll here */}
        <div 
          className="tf-slider" 
          ref={sliderRef} 
          onScroll={handleScroll}
        >
          {isLoading
            ? Array.from(new Array(6)).map((_, index) => (
                <SkeletonCard key={`skeleton-${index}`} />
              ))
            : groupData?.map((item, index) => (
                <div className="tf-card" key={`${item.tour.id}-${index}`}>
                  <div className="tf-image">
                    <img
                      src={
                        item?.tour?.images?.find((img) => img.is_featured)?.url ||
                        RajasthanImage
                      }
                      alt={item?.tour?.title}
                      loading="lazy" 
                    />
                    <div className="tf-overlay">
                      <h4>{item.tour.title}</h4>
                      <span>
                        {item.tour.no_of_days} Days <br />₹
                        {item.tour.raw_columns.mrp_group}
                      </span>
                    </div>
                  </div>

                  <div className="tf-content">
                    <div className="tf-footer">
                      <button onClick={() => handleGroupDetails(item)}>
                        View Tour
                      </button>
                      <small style={{ display: "flex" }}>
                        Last Date:{" "}
                        <DateFormatter
                          dateString={item?.tour?.dates?.booking_end_date}
                        />
                      </small>
                    </div>
                  </div>
                </div>
              ))}

          {/* Loading Skeleton at the end of the slider */}
          {isFetchingMore && (
            <div style={{ minWidth: "300px", height: "100%" }}>
              <SkeletonCard />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default Travelers_favorite;